import type { DocumentReviewData, ReviewPolygon, PolygonComment } from "@/types/document-review";

const LOCAL_STORAGE_KEY = "documentReviewPolygons";

// Load from localStorage or fallback to bundled JSON for test document
function loadInitialData(): Record<string, DocumentReviewData> {
    try {
        const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch {
        // ignore
    }
    return {};
}

let reviewDataByDocumentId: Record<string, DocumentReviewData> = loadInitialData();

function persist(): void {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reviewDataByDocumentId));
    } catch {
        // ignore persistence errors
    }
}

export const documentReviewService = {
    get(documentId: string): DocumentReviewData {
        if (!reviewDataByDocumentId[documentId]) {
            reviewDataByDocumentId[documentId] = { documentId, polygons: [] };
        }
        return reviewDataByDocumentId[documentId];
    },

    seed(documentId: string, polygons: ReviewPolygon[]): DocumentReviewData {
        reviewDataByDocumentId[documentId] = { documentId, polygons: [...polygons] };
        persist();
        return reviewDataByDocumentId[documentId];
    },

    addPolygon(documentId: string, polygon: Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">): ReviewPolygon {
        const data = this.get(documentId);
        const newPolygon: ReviewPolygon = {
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ...polygon,
        };
        data.polygons.push(newPolygon);
        persist();
        return newPolygon;
    },

    updatePolygon(documentId: string, polygonId: string, updates: Partial<Omit<ReviewPolygon, "id" | "createdAt">>): ReviewPolygon | null {
        const data = this.get(documentId);
        const idx = data.polygons.findIndex((p) => p.id === polygonId);
        if (idx === -1) return null;
        const updated: ReviewPolygon = {
            ...data.polygons[idx],
            ...updates,
            updatedAt: new Date().toISOString(),
        };
        data.polygons[idx] = updated;
        persist();
        return updated;
    },

    removePolygon(documentId: string, polygonId: string): boolean {
        const data = this.get(documentId);
        const before = data.polygons.length;
        data.polygons = data.polygons.filter((p) => p.id !== polygonId);
        const removed = data.polygons.length !== before;
        if (removed) persist();
        return removed;
    },

    addCommentToPolygon(documentId: string, polygonId: string, userId: string, userName: string, text: string): ReviewPolygon | null {
        const data = this.get(documentId);
        const polygon = data.polygons.find((p) => p.id === polygonId);
        if (!polygon) return null;

        const comment: PolygonComment = {
            id: crypto.randomUUID(),
            userId,
            userName,
            text,
            timestamp: new Date().toISOString(),
            // Alias per retrocompatibilità
            author: userName,
            createdAt: new Date().toISOString(),
        };

        polygon.comments = polygon.comments || [];
        polygon.comments.push(comment);
        polygon.updatedAt = new Date().toISOString();
        
        persist();
        return polygon;
    },
};
